/**
 * `set_alert_outcome` — who may mark an alert's outcome or edit its notes
 * (ENG-1810 → ENG-1813).
 *
 * The one place camera-ui names the `/user/info/` flag behind the True Threat /
 * Escalated / True Non-Threat / False Positive buttons and the notes editor.
 * actuate_admin exposes it as `permission.set_alert_outcome` (ENG-1811) and the
 * monitoring API checks the same permission on `alert/outcome/` and
 * `alert/unresolved/` (ENG-1812). Hiding the controls here only keeps the UI
 * honest about what the API will accept.
 */
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import { useAppStore } from "../../../../appStore";
import { logger } from "../../../../utils/logger";
import {
  APIErrorContext,
  universalAPIErrorHandler,
} from "../../../utils/universalAPIErrorHandler";

export const SET_ALERT_OUTCOME_FIELD = "set_alert_outcome";

export const ALERT_OUTCOME_FORBIDDEN_MESSAGE =
  "Your account can no longer set alert outcomes or edit alert notes. The controls have been hidden.";

/**
 * Only an explicit `false` hides the controls. userPermissions is cached in
 * localStorage and only refreshed at login, so a payload from before the
 * backend shipped the flag has no key at all; treating that as "cannot" would
 * hide the outcome buttons from every existing user until they next sign in.
 * The API enforces the permission regardless, and a 403 from it flips the
 * cached flag to false (revokeCachedAlertOutcomePermission).
 */
export const canSetAlertOutcome = (permissions: unknown): boolean =>
  (permissions as Record<string, unknown> | null | undefined)?.[
    SET_ALERT_OUTCOME_FIELD
  ] !== false;

// The monitoring API's 403 detail for a missing set_alert_outcome
// (monitoring/auth/alert_permissions.py, ENG-1812). Matched exactly so any
// other 403 (an expired session, "Authentication credentials were not
// provided.") still goes through universalAPIErrorHandler's refresh path.
export const ALERT_OUTCOME_FORBIDDEN_DETAIL =
  "You do not have permission to set alert outcomes.";

/**
 * A 403 from `alert/outcome/` or `alert/unresolved/` carrying the
 * set_alert_outcome denial. universalAPIErrorHandler would otherwise retry it
 * three times with backoff before giving up, so this is checked first.
 */
export const isAlertOutcomeForbidden = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;
  if (axiosError?.response?.status !== 403) return false;
  const data = axiosError.response?.data as { detail?: unknown } | undefined;
  return data?.detail === ALERT_OUTCOME_FORBIDDEN_DETAIL;
};

/**
 * The 403 means the cached flag is stale: the permission was revoked (or the
 * user was moved to View Only) after this tab's /user/info/ fetch. Flip the
 * store so the outcome buttons and notes editor un-render reactively, and keep
 * the localStorage mirror in step so the next boot doesn't resurrect them.
 * /user/info/ stays the source of truth; a re-grant shows on next login.
 * Mirrors revokeCachedLiveStreamingPermission in useWhepLiveStream.ts.
 */
export const revokeCachedAlertOutcomePermission = () => {
  try {
    const { userPermissions, setUserPermissions } = useAppStore.getState();
    if (canSetAlertOutcome(userPermissions)) {
      const next = { ...userPermissions, [SET_ALERT_OUTCOME_FIELD]: false };
      setUserPermissions(next);
      localStorage.setItem("userPermissions", JSON.stringify(next));
    }
  } catch (reason) {
    logger.warn("Could not revoke cached set_alert_outcome:", reason);
  }
};

/**
 * Error path for every outcome / notes write. A permission 403 revokes the
 * cached flag, runs `cleanup` (e.g. re-enable the buttons) and tells the user
 * once; anything else goes through universalAPIErrorHandler exactly as before.
 */
export const handleAlertOutcomeError = (
  reason: AxiosError,
  navigate: NavigateFunction,
  retryFunction: () => void,
  errorHandlingFunction: (context?: APIErrorContext) => void,
  cleanup?: () => void
) => {
  if (isAlertOutcomeForbidden(reason)) {
    cleanup?.();
    revokeCachedAlertOutcomePermission();
    alert(ALERT_OUTCOME_FORBIDDEN_MESSAGE);
    return;
  }
  universalAPIErrorHandler(
    reason,
    navigate,
    retryFunction,
    errorHandlingFunction
  );
};
